import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Style from './style';
import Widget from '../../widget';
import CustomProgressBar from '../../../../dashboards/components/RiskProgressBar';
import { getOptionLabel } from '../../../../DynamicViewNew/helper';
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';
import { Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const verifiedIcon = (color) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9.17715V10.0051C18.9989 11.9459 18.3704 13.8344 17.2084 15.3888C16.0463 16.9432 14.413 18.0804 12.5518 18.6307C10.6907 19.1809 8.70153 19.1149 6.88102 18.4423C5.06051 17.7697 3.50619 16.5266 2.44986 14.8985C1.39354 13.2704 0.891812 11.3444 1.01951 9.40783C1.14721 7.47126 1.89749 5.62784 3.15845 4.15252C4.41942 2.67719 6.1235 1.649 8.01657 1.22128C9.90963 0.79357 11.8902 0.989255 13.663 1.77915M19 2.8L10 11.809L7.3 9.109" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const renderIcon = (active, t = (s) => s) => active ? (
  <Tooltip title={t('Self-assessment conducted by the focus participant')}>
    <div className="icon active">
      <div className="layer">
        {verifiedIcon('#1570EF')}
      </div>
    </div>
  </Tooltip>
) : (
  <Tooltip title={t('Due diligence information is unavailable from the selected focus participant')}>
    <div className="icon">
      <div className="layer">
        {verifiedIcon('#E5E5E5')}
      </div>
    </div>
  </Tooltip>
)

const loader = (
  <div className="loading-ic">
    <LoadingOutlined />
  </div>
);

const Details = ({ data = null }) => {
  const { t } = useTranslation();
  const loading = !data;

  const options = useSelector((state) => state.project.data.options) || {};

  return (
    <Widget
      title={t('Focus Participant')}
      loading={false}
      withBorderHeader
    >
      <Style>
        <div className="flex-row details-header">
          <div className="flex-row">
            <div className="flex-c jus-c mr-2">
              <span>{t('Entity')}</span>
            </div>
            <div>
              {loading ? <LoadingOutlined /> : (
                <Tooltip
                  title={
                    t(
                      !data?.agent ? 'Due diligence information is unavailable from the selected focus participant'
                        : 'The selected focus participant reports due diligence information'
                    )
                  }>
                  <div className={formatClassname(["tag flex-row", !data?.agent && 'empty'])}>
                    <div className="flex-c jus-c">
                      <span>
                        {data?.agent || '--'}
                      </span>
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex-row">
            <div className="flex-row">
              <div className="flex-c jus-c mr-2">
                <span>{t('Product')}</span>
              </div>
              <div className="flex-c jus-c">
                {loading ? <LoadingOutlined /> : (
                  <div className={formatClassname(['tag flex-row gold', !data?.product && 'empty'])}>
                    <div className="flex-c jus-c">
                      <span>
                        {getOptionLabel((options.minerals || []), data?.product) || '--'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="details-body flex-c">
          <div className="flex-1">
            <div className="flex-1 mb-1">
              <span>{t('KYC Completion')}</span>
            </div>
            <CustomProgressBar
              withTooltip
              percentage={data?.kycProgress}
              color="#2E90FA"
            />
          </div>
          <div className="flex-1 mb-3">
            <div className="flex-1 mb-1">
              <span>{t('Counterparty Assessment')}</span>
            </div>
            <CustomProgressBar withTooltip percentage={loading ? 0 : data?.counterpartyScore} />
          </div>

          <div className="flex-1">
            <div className="flex-1 flex-c jus-c mb-2">
              <span>{t('Management Systems')}</span>
            </div>
            <div className="systems">
              <div className="flex-row flex-1">
                <div className="flex-1 flex-c jus-c">
                  <span>{t('Project::Supply Chain Policy Summary')}</span>
                </div>
                <div className="sys-icon flex-c jus-c">
                  {loading ? loader : renderIcon(!!data?.supplyChainPolicy, t)}
                </div>
              </div>

              <div className="flex-row flex-1">
                <div className="flex-1 flex-c jus-c">
                  <span>{t('Internal Management Summary')}</span>
                </div>
                <div className="sys-icon flex-c jus-c">
                  {loading ? loader : renderIcon(!!data?.internalManagement, t)}
                </div>
              </div>

              <div className="flex-row flex-1">
                <div className="flex-1 flex-c jus-c">
                  <span>{t('Supply Chain Integrity Summary')}</span>
                </div>
                <div className="sys-icon flex-c jus-c">
                  {loading ? loader : renderIcon(!!data?.supplyChainIntegrity, t)}
                </div>
              </div>

              <div className="flex-row flex-1">
                <div className="flex-1 flex-c jus-c">
                  <span>{t('Supplier Engagement Summary')}</span>
                </div>
                <div className="sys-icon flex-c jus-c">
                  {loading ? loader : renderIcon(!!data?.supplierEngagement, t)}
                </div>
              </div>

              <div className="flex-row flex-1">
                <div className="flex-1 flex-c jus-c">
                  <span>{t('Project::Grievance Mechanism Summary')}</span>
                </div>
                <div className="sys-icon flex-c jus-c">
                  {loading ? loader : renderIcon(!!data?.grievanceMechanism, t)}
                </div>
              </div>
            </div>

          </div>

        </div>
      </Style>
    </Widget>
  );
};

export default Details;
